import * as React from "react";
import { useEffect, useState } from "react";
import { Button, Gapped, Modal } from "react-ui";

import { declint } from "../../../Engine/utils";
import { CustomFile, selectUserFiles } from "../../../Helpers/file";
import {
    FILE_LOADER_CLICK,
    FILE_LOADER_FILES,
    FILE_LOADER_MANY_FILES_MODAL,
    FILE_LOADER_MANY_FILES_MODAL_ADD,
    FILE_LOADER_MANY_FILES_MODAL_CLOSE,
    FILE_LOADER_REPLACE,
} from "../../metricsAction";
import { LayoutEvents } from "../../Internals/LayoutEvents/LayoutEvents";
import { MetricsActionPayload, trackAction } from "../../Internals/Metrics/MetricsProvider";
import { PushInstance } from "../../Internals/ControlContexts/PathContexts/PushInstance";
import { AbsoluteResolvedModelPath, createAbsoluteResolvedFromTokens } from "../../../../../Common/ModelPath/ModelPath";
import { AttachmentInfoPath } from "../../../../../Common/PathConstants";
import { isChildOfType, ofReactType } from "../../TypingUtils";
import { useFormObject } from "../../FormObjectProvider";
import { getCandyHub } from "../../../Forms/Sentry/InitializeSentry";
import { AttachmentState } from "../../../Engine/Externals/Attachments/Store/AttachmentsReducer";

import { AddButton } from "./AddButton";
import { ButtonsContainer, ButtonsLine, DisabledText } from "./FileLoader.styles";
import { ReplaceButton } from "./ReplaceButton";
import { ViewFileContainer as ViewFile } from "./ViewFile/ViewFileContainer";
import { ConvertationInfo } from "./ViewFile/Convertation/ConvertationInfo";
import { ConvertationStatus, ConvertationType } from "./ViewFile/Convertation/Types";
import type { GosEdoChangeablePaths, GosEdoPaths } from "./types";

import { AddLine, Menu, ReplaceLine } from "./index";

export interface FileLoaderProps {
    hasFiles: boolean;
    bindingPath: AbsoluteResolvedModelPath;
    children: JSX.ElementClass;
    count: number;
    countExceededDescription?: string;
    extentions: readonly string[];
    maxSize?: number;
    AttachmentTypeHasCountOne: boolean;
    multiple: boolean;
    width?: number;
    downloadable: boolean;
    hideActions: boolean;
    instances: readonly string[];
    onReplaceFiles: (newFiles: CustomFile[] | CustomFile, replaceInstances: readonly string[]) => Promise<void>;
    onAddFiles: (files: CustomFile[] | CustomFile) => Promise<void>;
    onRemoveAttachment: () => void;
    attachments: AttachmentState;
    gosEdoPaths?: GosEdoPaths;
    convertToPdf?: boolean;
    changeableGosEdoPaths?: GosEdoChangeablePaths;
    isReadOnly?: boolean;
}

interface FileLoaderState {
    files: CustomFile[];
    showConfirmation: boolean;
}

function countFilesSelector({ bindingPath, count, instances }: FileLoaderProps): MetricsActionPayload {
    return { count: instances.length, maxCount: count, path: bindingPath.toLegacyPath() };
}

function pathSelector({ bindingPath }: FileLoaderProps): MetricsActionPayload {
    return { path: bindingPath.toLegacyPath() };
}

function filesSelector(
    { bindingPath }: FileLoaderProps,
    _: FileLoaderState,
    files: CustomFile[] | CustomFile
): MetricsActionPayload {
    let names: string[];
    if (Array.isArray(files)) {
        names = files.map(file => file.name);
    } else {
        names = [files.name];
    }
    return {
        path: bindingPath.toLegacyPath(),
        filesNames: names,
    };
}

export function FileLoader(props: FileLoaderProps): JSX.Element {
    const [stateFiles, setStateFiles] = useState<CustomFile[]>([]);
    const [stateShowConfirmation, setStateShowConfirmation] = useState<boolean>(false);

    const {
        instances,
        count = 0,
        countExceededDescription,
        AttachmentTypeHasCountOne,
        multiple,
        width,
        hasFiles,
    } = props;

    const form = useFormObject();
    const hasFilesRef = React.useRef<boolean>();
    const showConfirmation = stateShowConfirmation;
    const disabled = instances.length >= count;
    const children = React.Children.toArray(props.children);
    const viewFile = children.find(ofReactType(ViewFile));

    const [convertationStatus, setConvertationStatus] = useState(ConvertationStatus.Idle);

    const convertationType = props.gosEdoPaths
        ? ConvertationType.PdfA1_1_4
        : props.convertToPdf
          ? ConvertationType.Pdf
          : undefined;

    useEffect(() => {
        LayoutEvents.emit();
    }, [props.instances.length]);

    useEffect(() => {
        hasFilesRef.current = hasFiles;
    }, [hasFiles]);

    useEffect(
        () => () => {
            if (!hasFilesRef.current) {
                props.onRemoveAttachment();
            }
        },
        []
    );

    useEffect(() => {
        let totalSize = 0;
        for (const key of Object.keys(props.attachments.byId)) {
            const attachmentValue = props.attachments.byId[key];
            if (attachmentValue !== undefined) {
                const fileSize = attachmentValue.meta.fileSize;
                totalSize += Number(fileSize);
            }
        }

        form.setValue(
            createAbsoluteResolvedFromTokens([form.settings.formRoot, "totalSizeFileLoader"]),
            totalSize.toString()
        );
    }, [instances]);

    async function selectAndReplaceFiles(replaceInstances: readonly string[]): Promise<void> {
        try {
            if (!multiple) {
                const singleFile = await selectUserFiles(false);
                const [file] = singleFile;
                if (file != undefined) {
                    setConvertationStatus(ConvertationStatus.Idle);
                    await props.onReplaceFiles(file, replaceInstances);
                }
            } else {
                const files = await selectUserFiles(AttachmentTypeHasCountOne);
                await props.onReplaceFiles(files, replaceInstances);
            }
        } catch (error) {
            getCandyHub().captureException(error);
        }
    }

    async function selectAndAddFiles(): Promise<void> {
        try {
            if (!multiple) {
                const singleFile = await selectUserFiles(false);
                const [file] = singleFile;
                if (file != undefined) {
                    await addFiles(file);
                }
            } else {
                const files = await selectUserFiles(AttachmentTypeHasCountOne);
                if (count > 0 && instances.length + files.length > count) {
                    setStateFiles(files.slice(0, count - instances.length));
                    setStateShowConfirmation(true);
                } else {
                    await addFiles(files);
                }
            }
        } catch (error) {
            getCandyHub().captureException(error);
        }
    }

    async function addFiles(files: CustomFile[] | CustomFile): Promise<void> {
        trackAction(FILE_LOADER_FILES, filesSelector);
        await props.onAddFiles(files);
    }

    const closeConfirmation = (): void => {
        trackAction(FILE_LOADER_MANY_FILES_MODAL_CLOSE, countFilesSelector);
        setStateFiles([]);
        setStateShowConfirmation(false);
    };

    const handleOpenFileDialog = async (): Promise<void> => {
        trackAction(FILE_LOADER_CLICK, pathSelector);
        await selectAndAddFiles();
    };

    const handleReplaceFiles = async (): Promise<void> => {
        trackAction(FILE_LOADER_REPLACE, pathSelector);
        await selectAndReplaceFiles(props.instances);
    };

    const addFilesAsPossible = async (): Promise<void> => {
        trackAction(FILE_LOADER_MANY_FILES_MODAL_ADD, countFilesSelector);
        const files = stateFiles;

        setStateFiles([]);
        setStateShowConfirmation(false);
        await addFiles(files);
    };

    function renderConfirmation(): JSX.Element {
        trackAction(FILE_LOADER_MANY_FILES_MODAL, countFilesSelector);
        return (
            <Modal width={500} onClose={closeConfirmation}>
                <Modal.Header>{"Слишком много документов"}</Modal.Header>
                <Modal.Body>
                    {`В форму можно загрузить только ${declint(count, [
                        "%s документ",
                        "%s документа",
                        "%s документов",
                    ])}.`}
                    <br />
                    {countExceededDescription}
                </Modal.Body>
                <Modal.Footer panel>
                    <Gapped gap={10}>
                        <Button size="medium" use="primary" onClick={addFilesAsPossible}>
                            Загрузить сколько входит
                        </Button>
                        <Button size="medium" onClick={closeConfirmation}>
                            Отменить
                        </Button>
                    </Gapped>
                </Modal.Footer>
            </Modal>
        );
    }

    function renderButton(childrenProp: React.ReactNode[]): JSX.Element {
        const hasAttachment = Boolean(props.instances.length);

        if (!multiple && hasFiles) {
            return <ReplaceButton onClick={handleReplaceFiles}>Заменить документ</ReplaceButton>;
        }

        const menu = childrenProp.find(ofReactType(Menu));
        const line =
            AttachmentTypeHasCountOne || !hasAttachment
                ? menu && menu.props.children.find(ofReactType(AddLine))
                : menu && menu.props.children.find(ofReactType(ReplaceLine));

        if (line) {
            const lineChildren = React.Children.map(line.props.children, child => {
                if (isChildOfType(child, AddButton)) {
                    return React.cloneElement(child, {
                        ...child.props,
                        onClick: handleOpenFileDialog,
                        disabled: disabled,
                    });
                }

                if (isChildOfType(child, ReplaceButton)) {
                    return React.cloneElement(child, {
                        ...child.props,
                        onClick: handleReplaceFiles,
                    });
                }

                return child;
            });

            return (
                <ButtonsLine>
                    {!props.isReadOnly ? lineChildren : !hasAttachment ? "Нет файлов для просмотра" : null}
                </ButtonsLine>
            );
        }

        if (AttachmentTypeHasCountOne || !hasAttachment) {
            return (
                <AddButton disabled={disabled} onClick={handleOpenFileDialog}>
                    Загрузить документы
                </AddButton>
            );
        } else {
            return <ReplaceButton onClick={handleReplaceFiles}>Заменить документ</ReplaceButton>;
        }
    }

    const viewFiles = instances.map((instance: string) => {
        const currentProps = {
            children: viewFile && viewFile.props.children,
            attachmentInstance: instance,
            parentPath: props.bindingPath,
            bindingPath: props.bindingPath.joinWith(instance).joinWith(AttachmentInfoPath),
            width: props.width,
            downloadable: props.downloadable,
            hideActions: props.hideActions,
            extentions: props.extentions,
            convertationStatus: convertationStatus,
            setConvertationStatus: setConvertationStatus,
            convertationType: convertationType,
            gosEdoPaths: props.gosEdoPaths,
            changeableGosEdoPaths: props.changeableGosEdoPaths,
        };

        return (
            <PushInstance key={instance} instance={instance} bindingPath={props.bindingPath}>
                {viewFile ? React.cloneElement(viewFile, currentProps) : <ViewFile {...currentProps} />}
            </PushInstance>
        );
    });

    return (
        <div style={width ? { width: `${width}px` } : undefined}>
            {showConfirmation && renderConfirmation()}
            {!multiple && hasFiles ? (
                <ViewFile
                    parentPath={props.bindingPath}
                    bindingPath={props.bindingPath.joinWith(AttachmentInfoPath)}
                    width={props.width}
                    downloadable={props.downloadable}
                    hideActions={props.hideActions}
                    gosEdoPaths={props.gosEdoPaths}
                    changeableGosEdoPaths={props.changeableGosEdoPaths}
                    convertationStatus={convertationStatus}
                    setConvertationStatus={setConvertationStatus}
                    extentions={props.extentions}
                    convertationType={convertationType}>
                    {viewFile && viewFile.props.children}
                </ViewFile>
            ) : (
                viewFiles
            )}
            <ButtonsContainer>{renderButton(children)}</ButtonsContainer>
            {props.convertToPdf && (
                <DisabledText>
                    Допустимые для загрузки форматы: {props.extentions.map(ext => `.${ext}`).join(", ")}
                </DisabledText>
            )}
            {props.gosEdoPaths && (
                <ConvertationInfo
                    status={convertationStatus}
                    onStatusReset={() => setConvertationStatus(ConvertationStatus.Idle)}
                />
            )}
            {disabled && AttachmentTypeHasCountOne ? (
                <DisabledText data-tid="fileloader-overflow-documents">
                    {`К этой форме можно приложить до ${declint(count, [
                        "%s документа",
                        "%s документов",
                        "%s документов",
                    ])}. `}
                    {countExceededDescription}
                </DisabledText>
            ) : undefined}
        </div>
    );
}

FileLoader.defaultProps = { count: 0 };
